import { useCallback, useRef } from "react";
import { EVENTS } from "@bash/shared";
import { useConnectionStore } from "@/stores/connectionStore";
import { useTaskStore } from "@/stores/taskStore";

const TYPING_SESSION_GAP_MS = 1500;
const TYPING_TOUCH_THROTTLE_MS = 1000; // ~1Hz, mirrors the co-browse heartbeat

/**
 * Pre-warm the sandbox at the start of each "typing session" on a task's
 * screen, carrying the TASK so the sandbox pre-spawns a WarmQuery for it (the
 * home composer's useComposerPrewarm carries none, by design). Moved out of
 * task/layout.tsx verbatim so the layout's box and the Dock's pill warm by one
 * rule.
 *
 * A typing session = the first keystroke after >1.5s of keyboard silence.
 * This means: rapid typing within one message fires ONE pre-warm; opening a
 * new task and typing fires another; typing, pausing to think, then typing
 * more also fires another.
 *
 * Time-based, not connection-state-based: the server's pre-warm flow
 * transitions connectionStatus (offline → starting → idle) for visible
 * feedback, and a state-change reset (the implementation before this one)
 * would loop: the server transitions to 'idle', the frontend ref resets, the
 * next keystroke fires again, the server re-transitions, and so on. Time-based
 * tracking breaks the loop cleanly.
 */
export function useTaskPrewarm(): () => void {
  const lastKeystrokeAt = useRef(0);
  const lastTypingTouchAt = useRef(0);

  return useCallback(() => {
    const now = Date.now();

    // Typing-hold: a throttled ~1Hz keep-warm heartbeat, INDEPENDENT of the
    // pre-warm session dedup below (that fires once per typing session; this
    // repeats while the person keeps typing). The server forwards it to the
    // sandbox as an activity touch ONLY when the relay is live (dead relays
    // drop it), so waking stays the SANDBOX_PRE_WARM path's job. Composing
    // counts as working: this defers the post-task idle timer so the sandbox
    // stays hot while a message is still being written.
    if (now - lastTypingTouchAt.current >= TYPING_TOUCH_THROTTLE_MS) {
      lastTypingTouchAt.current = now;
      useConnectionStore.getState().getSocket()?.emit(EVENTS.SANDBOX_TYPING);
    }

    const isNewSession = now - lastKeystrokeAt.current > TYPING_SESSION_GAP_MS;
    lastKeystrokeAt.current = now;
    if (!isNewSession) return;

    // Don't fire if the sandbox is currently mid-work. 'idle' IS allowed: the
    // server treats an already-warm sandbox as a quick no-op with just the
    // display transition for feedback.
    const status = useConnectionStore.getState().connectionState.status;
    if (status === "active" || status === "starting" || status === "updating")
      return;

    const socket = useConnectionStore.getState().getSocket();
    // Carry task context so the sandbox pre-spawns a WarmQuery for THIS
    // specific task, not a task-agnostic boot pre-warm: that was the
    // 2026-04-20 bug class. agentSlug is read from the task store so options
    // match the real USER_MESSAGE path byte for byte.
    //
    // For NEW tasks (selectedTaskId === null), allocate a stable draft UUID and
    // pass it to the sandbox; the sandbox initializes the SDK session for that
    // exact UUID and sendMessage consumes the same UUID at send time, so the
    // warm session matches. That is what kills the ~3.5s SDK init cost on the
    // first message of every new task.
    const taskState = useTaskStore.getState();
    const existingTaskId = taskState.selectedTaskId;
    const taskId = existingTaskId ?? taskState.getOrAllocatePendingNewTaskId();
    const agentSlug = existingTaskId
      ? taskState.tasks.get(existingTaskId)?.agentSlug
      : undefined;
    socket?.emit(EVENTS.SANDBOX_PRE_WARM, {
      taskId,
      agentSlug,
    });
  }, []);
}
